/** FN-2b single-flight coordinator. All asynchronous work precedes one commit. */
import { canonicalStringify } from '../shared/collab-protocol.js';
import { reportError } from '../core/report-error.js';

export const EDITABLE_ID_REPAIR_LABEL = 'Resolve duplicate editable IDs';

function sectionAt(state, path) {
  return path.split('.').reduce((value, key) => value?.[key], state);
}

export function createEditableIdRepairAction(deps) {
  let inFlight = false;
  return async function repairEditableIds() {
    if (inFlight) return { kind: 'busy' };
    inFlight = true;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 30_000);
    const refuse = (reason) => {
      deps.notify(`Element identity repair could not continue: ${reason}`, { type: 'info' });
      return { kind: 'refused', reason };
    };
    try {
      deps.pending?.(true);
      const { store } = deps;
      if (!store.isWritable()) return refuse('this project is read-only');
      const editing = () => store.isInTransaction?.() || deps.getActiveTextEdit?.();
      if (editing()) return refuse('finish the active edit and try again');
      const initial = store.getState();
      if (!initial?.projectId) return refuse('the project identity is unknown');
      const projectId = initial.projectId;
      const account = deps.getUser()?.sub ?? null;
      const generation = store.getEpoch() - store.getRevision();
      const sameProject = () =>
        store.getState()?.projectId === projectId &&
        store.getEpoch() - store.getRevision() === generation &&
        (deps.getUser()?.sub ?? null) === account;
      const hydrated = await deps.hydrate({ signal: controller.signal });
      if (editing()) return refuse('finish the active edit and try again');
      if (!sameProject() || controller.signal.aborted)
        return refuse('the project or account changed');
      if (
        !hydrated?.coverage ||
        hydrated.coverage.pagesHydrated !== hydrated.coverage.pagesTotal ||
        hydrated.coverage.hydrationFailed.length
      ) {
        return refuse('some pages could not be loaded; retry when every page is available');
      }
      const snapshot = structuredClone(store.getState());
      const epoch = store.getEpoch();
      const revision = store.getRevision();
      const current = () =>
        sameProject() &&
        store.getEpoch() === epoch &&
        store.isWritable() &&
        !controller.signal.aborted;
      const namespace = deps.namespaceOf(snapshot);
      if (namespace === 'unknown')
        return refuse(
          'this copy has no confirmed comment namespace; save it to the cloud before repairing'
        );
      let threads = [];
      const context = {
        projectId,
        cloudProject: namespace === 'cloud',
        sharingKnown: namespace === 'local',
        shared: false
      };
      if (namespace === 'cloud') {
        const [comments, membership] = await Promise.all([
          deps.listComments(projectId, { since: null, signal: controller.signal }),
          deps.listMembers(projectId, { signal: controller.signal })
        ]);
        if (!current()) return refuse('the project or account changed while comments were loading');
        if (
          comments?.ok !== true ||
          comments.complete !== true ||
          comments.projectId !== projectId ||
          comments.anchorIdentityVersion !== 1 ||
          !Array.isArray(comments.threads)
        ) {
          return refuse('the complete versioned comment inventory is unavailable');
        }
        if (
          membership?.ok !== true ||
          !Array.isArray(membership.members) ||
          !membership.members.some(
            (member) => member.sub === account && ['owner', 'editor'].includes(member.role)
          )
        ) {
          return refuse('project membership could not be confirmed');
        }
        threads = comments.threads;
        context.sharingKnown = true;
        context.shared =
          membership.members.length > 1 ||
          membership.members.some((member) => member.sub === account && member.role !== 'owner');
      }
      const admitted = deps.readiness(context);
      if (!admitted?.ok)
        return refuse(admitted?.message || admitted?.reason || 'the live session is not ready');
      const templateCSS = (await deps.templateCSS?.(snapshot, { signal: controller.signal })) || '';
      if (!current()) return refuse('the project changed while template styles were loading');
      const plan = await deps.plan(snapshot, { projectId, threads, templateCSS });
      if (!current()) return refuse('the project changed while identities were being checked');
      if (editing()) return refuse('finish the active edit and try again');
      const ready = deps.readiness(context);
      if (!ready?.ok || ready.roomEpoch !== admitted.roomEpoch || ready.atSeq !== admitted.atSeq) {
        return refuse('the live session changed; wait for it to sync and try again');
      }
      for (const section of plan.sections) {
        if (
          canonicalStringify(sectionAt(store.getState(), section.sectionPath)) !==
          canonicalStringify(section.before)
        ) {
          return refuse('an affected section changed');
        }
        const lock = section.sectionPath.startsWith('sharedSections.')
          ? `shared:${section.sectionPath.split('.')[1]}`
          : `s:${section.before.instanceId}`;
        if (deps.isLocked(lock)) return refuse('another editor holds an affected section');
      }
      deps.captureRefusals?.(plan.refusals, snapshot);
      if (!plan.forward.length) {
        const reason = plan.refusals.length
          ? plan.refusals.map((refusal) => refusal.reason).join('; ')
          : 'no duplicate IDs remain';
        return refuse(reason);
      }
      if (editing()) return refuse('finish the active edit and try again');
      const committed = store.applyCompound({
        forward: plan.forward,
        reverse: plan.reverse,
        label: EDITABLE_ID_REPAIR_LABEL,
        expectedRevision: revision,
        options: { identityRepairContext: context }
      });
      if (!committed) return refuse('the captured repair could not be committed');
      const instanceIds = plan.sections.map((section) => section.before.instanceId);
      deps.invalidate(instanceIds);
      const message =
        `${plan.replacedCount} duplicate ID${plan.replacedCount === 1 ? '' : 's'} repaired` +
        (plan.refusals.length
          ? ` · ${plan.refusals.length} section${plan.refusals.length === 1 ? '' : 's'} need manual review`
          : '');
      deps.notify(message, { type: 'success', undo: true, refusals: plan.refusals });
      return { kind: 'repaired', ...plan };
    } catch (error) {
      reportError('Element identity repair', error, { user: 'silent' });
      return refuse(
        controller.signal.aborted
          ? 'the request timed out; try again'
          : error.message || 'the required data could not be read'
      );
    } finally {
      clearTimeout(timer);
      controller.abort();
      inFlight = false;
      deps.pending?.(false);
    }
  };
}
